import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { EVENTOS } from '@crm/shared'
import { chaves } from '../api/hooks'
import { pegarSocket, soltarSocket } from '../tempo-real/socket'
import { Botao } from './ui'
import { marcarSaidaPeloHorario } from './FimDoHorario'

/**
 * Quando o acesso da pessoa muda com o CRM aberto (mapa de uso, 08/10/2026).
 *
 * Antes, tirar uma permissão de alguém só valia quando a pessoa recarregava a
 * página: até lá ela continuava vendo o menu inteiro e a lista que já estava
 * carregada. Pela decisão do Mike, a tela agora trava com o aviso e "não pode
 * burlar, só consegue continuar mexendo no CRM se recarregar": a camada cobre
 * tudo, não fecha com Esc nem com clique fora, e o único botão recarrega.
 *
 * A sessão encerrada (pessoa desativada, senha redefinida) não pede nada: a
 * tela confere a sessão na hora e cai no login.
 */
export function AvisoDeAcesso() {
  const cliente = useQueryClient()
  const [alterado, setAlterado] = useState(false)

  useEffect(() => {
    const socket = pegarSocket()
    const aoAlterar = () => setAlterado(true)
    const aoEncerrar = (dados?: { motivo?: string }) => {
      // Caiu pelo horário de trabalho: o login conta o motivo (10/10/2026).
      if (dados?.motivo === 'fim_do_horario') marcarSaidaPeloHorario()
      void cliente.invalidateQueries({ queryKey: chaves.eu })
    }
    socket.on(EVENTOS.acessoAlterado, aoAlterar)
    socket.on(EVENTOS.sessaoEncerrada, aoEncerrar)
    return () => {
      socket.off(EVENTOS.acessoAlterado, aoAlterar)
      socket.off(EVENTOS.sessaoEncerrada, aoEncerrar)
      soltarSocket()
    }
  }, [cliente])

  // Travado, o teclado também para: nada de Tab até um campo atrás da camada.
  useEffect(() => {
    if (!alterado) return
    const segurar = (evento: KeyboardEvent) => {
      // Enter aciona o Recarregar; F5 e Ctrl+R recarregam pelo navegador.
      const recarrega =
        evento.key === 'F5' ||
        ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'r')
      if (evento.key === 'Enter' || recarrega) return
      evento.stopPropagation()
      evento.preventDefault()
    }
    window.addEventListener('keydown', segurar, true)
    return () => window.removeEventListener('keydown', segurar, true)
  }, [alterado])

  if (!alterado) return null

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="acesso-alterado-titulo"
      className="fixed inset-0 z-[200] flex items-center justify-center whitespace-normal bg-fundo-escuro/60 p-4 text-left text-base text-tinta-900 backdrop-blur-sm print:hidden"
    >
      <div className="w-full max-w-sm rounded-xl bg-superficie p-5 shadow-alta">
        <h2 id="acesso-alterado-titulo" className="text-base font-semibold tracking-tight">
          Seu acesso mudou
        </h2>
        <p className="mt-2 text-sm text-tinta-600">
          Quem administra o CRM mudou o que você pode ver e fazer. Recarregue a página para
          continuar com o acesso novo.
        </p>
        <div className="mt-4 flex justify-end">
          <Botao variante="primario" autoFocus onClick={() => window.location.reload()}>
            Recarregar
          </Botao>
        </div>
      </div>
    </div>,
    document.body,
  )
}
